import { Element, Node, Text } from '../../core';
import { PARAGRAPH_PLUGIN_ID } from '../paragraph/types';
import { HEADING_PLUGIN_ID } from '../heading/types';
import { LIST_PLUGIN_ID } from '../list/types';

/**
 * RP-6069 (M8a): pure, idempotent transform that converts legacy `heading` nodes and
 * inline paragraphs into plain paragraphs — the on-load representation flip for
 * headings-as-decoration. Not wired anywhere yet (M8b runs it on load/normalize).
 *
 *  - `heading` (H1 or H2) -> `paragraph` (former H2 is just a heading now).
 *  - `heading` + trailing `shouldForceInline` paragraph(s) -> ONE paragraph whose children
 *    are concatenated. Because an inline paragraph joins directly onto the previous node
 *    when flattened (see `slateToString`) and `createInlineParagraph` carries its own
 *    leading space, concatenation preserves the flattened `"Label: value"` string exactly.
 *  - A stray inline paragraph (not consumed by a heading) -> plain paragraph.
 *  - Recurses into `list` and `field` children.
 *
 * Idempotent: output has no `heading` nodes and no `shouldForceInline` paragraphs, so a
 * second pass is a no-op.
 *
 * INBOUND ONLY. This is the load/into-the-app direction. The reverse (editor paragraphs ->
 * server `field` sections/labels) is `convertUnifiedToFields`, already made
 * representation-agnostic in M5a (RP-6068) and wired into save/autosave in M8c (RP-6011).
 */

const typeOf = (node: Node): string | undefined =>
  Element.isElement(node) ? (node as { type?: string }).type : undefined;

const childrenOf = (node: Node): Node[] =>
  Element.isElement(node) ? ((node as { children?: Node[] }).children ?? []) : [];

const isHeadingNode = (node: Node): boolean => typeOf(node) === HEADING_PLUGIN_ID;

const isInlineParagraph = (node: Node): boolean =>
  typeOf(node) === PARAGRAPH_PLUGIN_ID &&
  (node as { shouldForceInline?: boolean }).shouldForceInline === true;

const isContainer = (node: Node): boolean =>
  typeOf(node) === LIST_PLUGIN_ID || typeOf(node) === 'field';

const marksOf = (text: Text): Record<string, unknown> => {
  const { text: _discardText, ...marks } = text as { text: string } & Record<string, unknown>;
  return marks;
};

// Order-independent mark equality. `JSON.stringify` is key-order-sensitive (`{a,b}` vs `{b,a}`),
// which could wrongly keep two same-marked text nodes separate; compare keys/values directly.
const sameMarks = (a: Record<string, unknown>, b: Record<string, unknown>): boolean => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  return (
    aKeys.length === bKeys.length &&
    aKeys.every((key) => Object.hasOwn(b, key) && Object.is(a[key], b[key]))
  );
};

// A saved legacy H1 can carry a `headingError` mark. Once it is a paragraph nothing clears it
// (the headingError plugin only matches H1 nodes), so drop it here; the content-derived sign
// gate (useSyncHeadingErrorState) still detects duplicates.
const stripHeadingError = (children: Node[]): Node[] =>
  children.map((child) => {
    if (!Text.isText(child) || !('headingError' in child)) return child;
    const { headingError: _discardHeadingError, ...rest } = child as Text & {
      headingError?: boolean;
    };
    return rest as Node;
  });

// Merge consecutive plain text nodes that share the same marks, so a label + inline value
// collapse into a single `{ text: 'Label: value' }` rather than two adjacent text nodes.
const mergeAdjacentText = (children: Node[]): Node[] => {
  const merged: Node[] = [];
  for (const child of children) {
    const prev = merged[merged.length - 1];
    if (
      Text.isText(child) &&
      prev != null &&
      Text.isText(prev) &&
      sameMarks(marksOf(prev), marksOf(child))
    ) {
      merged[merged.length - 1] = { ...prev, text: prev.text + child.text };
    } else {
      merged.push(child);
    }
  }
  return merged;
};

const asParagraph = (children: Node[]): Node =>
  ({ type: PARAGRAPH_PLUGIN_ID, children: mergeAdjacentText(children) }) as unknown as Node;

export const translateLegacyHeadings = (content: Node[]): Node[] => {
  const result: Node[] = [];

  for (let i = 0; i < content.length; i++) {
    const node = content[i];

    if (isContainer(node)) {
      result.push({
        ...(node as object),
        children: translateLegacyHeadings(childrenOf(node)),
      } as unknown as Node);
      continue;
    }

    if (isHeadingNode(node)) {
      let mergedChildren = stripHeadingError(childrenOf(node));
      let next = i + 1;
      while (next < content.length && isInlineParagraph(content[next])) {
        mergedChildren = mergedChildren.concat(childrenOf(content[next]));
        next++;
      }
      result.push(asParagraph(mergedChildren));
      i = next - 1; // skip the inline paragraph(s) folded into the heading
      continue;
    }

    if (isInlineParagraph(node)) {
      // A STRAY inline paragraph (no preceding heading to fold into) becomes a plain paragraph.
      // The flatten-string invariant only holds for heading-anchored inline paragraphs; a truly
      // detached inline paragraph is malformed input (nothing produces it), and de-inlining it here
      // changes its flattened newline. Accepted — inline paragraphs are only ever heading-attached.
      result.push(asParagraph(childrenOf(node)));
      continue;
    }

    result.push(node);
  }

  return result;
};
